'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AppLogo from '@/components/ui/AppLogo';
import { Zap, Star, Shield, TrendingUp } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import Icon from '@/components/ui/AppIcon';


export default function AuthLayout() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const { t } = useLanguage();

  const benefits = [
    { icon: Star, text: t?.auth?.benefit1 },
    { icon: TrendingUp, text: t?.auth?.benefit2 },
    { icon: Shield, text: t?.auth?.benefit3 },
    { icon: Zap, text: t?.auth?.benefit4 },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row relative overflow-hidden">
      {/* Background orbs */}
      <div className="orb-primary w-96 h-96 top-0 left-0 opacity-40" />
      <div className="orb-accent w-64 h-64 bottom-0 right-0 opacity-30" />
      {/* Left Brand Panel */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-5/12 flex-col justify-between p-12 relative border-r border-border/50"
        style={{ background: 'linear-gradient(160deg, #1A0A3E 0%, #0F0A1E 100%)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <span className="font-extrabold text-xl text-foreground">
            VoltVision<span className="text-accent">18</span>
          </span>
        </div>

        {/* Hero */}
        <div>
          <h1 className="text-4xl xl:text-5xl font-extrabold text-foreground leading-tight mb-4">
            {t?.auth?.stopGuessing}
            <br />
            <span className="text-gradient-primary">{t?.auth?.startCharging}</span>
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed max-w-sm mb-8">
            {t?.auth?.brandDesc}
          </p>

          {/* Benefits */}
          <div className="flex flex-col gap-3">
            {benefits?.map((benefit) => {
              const Icon = benefit?.icon;
              return (
                <div key={`benefit-${benefit?.text}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <Icon size={14} className="text-primary" />
                  </div>
                  <span className="text-sm text-foreground font-medium">{benefit?.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Testimonial */}
        <div className="rounded-xl border border-border/60 bg-muted/10 p-5">
          <p className="text-sm text-foreground italic leading-relaxed mb-3">
            {t?.auth?.testimonial}
          </p>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center">
              <span className="text-xs font-bold text-white">M</span>
            </div>
            <div>
              <div className="text-xs font-bold text-foreground">{t?.auth?.testimonialName}</div>
              <div className="text-[10px] text-muted-foreground">{t?.auth?.testimonialHandle}</div>
            </div>
            <div className="ml-auto flex gap-0.5">
              {[0, 1, 2, 3, 4]?.map((i) => (
                <Star key={`star-${i}`} size={10} className="text-accent fill-accent" />
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 lg:p-12">
        {/* Mobile Logo */}
        <div className="flex lg:hidden items-center gap-2 mb-8">
          <AppLogo size={28} />
          <span className="font-extrabold text-lg text-foreground">
            VoltVision<span className="text-accent">18</span>
          </span>
        </div>

        <div className="w-full max-w-sm">
          {/* Tab Toggle */}
          <div className="flex rounded-xl border border-border bg-muted/20 p-1 mb-7">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                mode === 'login' ?'bg-primary text-primary-foreground shadow-lg' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t?.auth?.signIn}
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                mode === 'signup' ?'bg-primary text-primary-foreground shadow-lg' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t?.auth?.createAccount}
            </button>
          </div>

          {mode === 'login' ? (
            <LoginForm onSwitchToSignup={() => setMode('signup')} />
          ) : (
            <SignUpForm onSwitchToLogin={() => setMode('login')} />
          )}

          {/* Guest CTA */}
          <div className="mt-6 text-center">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground font-medium">{t?.auth?.or}</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <a
              href="/"
              className="block w-full py-3 rounded-xl border border-border/60 text-sm font-semibold text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/5 transition-all duration-150 text-center"
            >
              {t?.auth?.continueWithoutAccount}
            </a>
            <p className="text-[11px] text-muted-foreground mt-2">
              {t?.auth?.noSaveWarning}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}